import { ReactNode, useEffect, useState } from 'react';
import {
  ButtonText,
  Subtitle,
  LabelText,
  BodyText,
} from 'components/UI/Brand/text';
import clsx from 'clsx';
import { Link, useNavigate } from 'react-router-dom';
import ArrowSearchLink from 'components/UI/Links/ArrowSearchLink';
import ContentfulRenderer from 'components/ContentfulRenderer';
import Icon from 'components/UI/Icons/Icon';
import { getToken } from 'utils/getToken';
import CustomImage from 'components/UI/CustomImage/CustomImage';
import DestinationCard from 'components/UI/Cards/DestinationCard/DestinationCard';
import MarketingDropdown, {
  IDropdownOption,
} from 'components/UI/Select/MarketingDropdown';
import { IGeneralSection } from 'interfaces/IMarketing';
import { IButton } from 'interfaces/IButton';
import { AlertBox, LinkText } from 'components';
import { PrimaryButton } from 'components/UI/Buttons/PrimaryButton/PrimaryButton';
import SectionLayout from '../SectionLayout';
import { ImageCroppings } from '../ImageCroppings';
import ButtonTypes from '../ButtonTypes';
import { HEADER_SIZE } from '../utils/getHeader.util';
import styles from './styles.module.scss';

interface ISectionProps {
  article?: IGeneralSection;
  className?: string;
  onDownloadClick?: (value: string) => void;
  empty?: boolean;
  needMarginRight?: boolean;
  highestHeadingSize?: HEADER_SIZE;
}

const Section = (props: ISectionProps) => {
  const {
    className,
    article,
    empty,
    onDownloadClick,
    needMarginRight,
    highestHeadingSize = HEADER_SIZE.H1,
  } = props;
  const navigate = useNavigate();
  let containerImageClass = '';
  let textImageClass = '';
  let imageClass = '';
  let containerClass = '';
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const tokenData = getToken();

  const buttonOnClickHandler = (button: IButton, analyticsLabel = '') => {
    if (button.onClick) {
      button.onClick();
    }

    if (button.type === ButtonTypes.NavigationLink) {
      navigate(button.navigationLink);
    }

    if (
      button.linkType === ButtonTypes.Link ||
      button.linkType === ButtonTypes.File
    ) {
      window.open(button.url || button.linkedFile);
    }

    if (button.linkType === ButtonTypes.Infox) {
      const url = `${button?.url?.trim()}?accessToken=${tokenData}`;
      window.open(url);
    }

    if (button.linkedFile) {
      onDownloadClick(analyticsLabel || article.title);
    }
  };

  useEffect(() => {
    const mobileVersion = window.innerWidth < 600;

    if (!empty) {
      if (mobileVersion) {
        if (article.croppingSize === ImageCroppings.Tall) {
          setImageWidth(window.innerWidth);
          const calculatedHeight = (window.innerWidth / 9) * 10;
          setImageHeight(Math.round(calculatedHeight));
        }
      } else if (article.croppingSize === ImageCroppings.Tall) {
        setImageWidth(360);
        setImageHeight(520);
      } else if (article.croppingSize === ImageCroppings.Wide) {
        setImageWidth(368);
        setImageHeight(207);
      } else if (article.croppingSize === ImageCroppings.Square) {
        setImageWidth(351);
        setImageHeight(351);
      }
    }
  }, [window.innerWidth]);

  if (empty) {
    return (
      <div
        className={clsx(styles.empty, className, {
          [styles.needMarginRight]: needMarginRight,
        })}
      />
    );
  }

  switch (article.layout) {
    case SectionLayout.Right: {
      containerImageClass = styles.containerImageRight;
      textImageClass = styles.textContainerImageRight;
      containerClass = styles.contentContainerRight;
      break;
    }
    case SectionLayout.Left: {
      textImageClass = styles.textContainerImageLeft;
      containerClass = styles.contentContainerLeft;
      break;
    }
    case SectionLayout.Top: {
      textImageClass = `${styles.textContainerImageTop}, ${
        article?.buttons ? styles.hasArticleButton : ''
      }`;
      containerImageClass = styles.containerImageTop;
      imageClass = styles.imageTop;
      containerClass = styles.contentContainerTop;
      break;
    }
    default: {
      containerClass = styles.contentContainerTop;
      break;
    }
  }

  if (article.layout === SectionLayout.Behind) {
    return (
      <DestinationCard
        description={article.shortDescription as unknown as ReactNode}
        title={article.title}
        image={article.media || ''}
      >
        {article.buttons && article.buttons.length > 0 && (
          <div className={styles.destinationCardButtonContainer}>
            {article.buttons.map((button) => (
              <PrimaryButton
                key={button.navigationLink}
                onClick={() => buttonOnClickHandler(button)}
                size="normal"
              >
                {button.title}
              </PrimaryButton>
            ))}
          </div>
        )}
      </DestinationCard>
    );
  }

  const getButtonOrDropdown = () => {
    const { button, buttons, dropdownList } = article;

    if (dropdownList) {
      const { title, linkItems } = dropdownList;
      const onClick = (item: IDropdownOption) => {
        buttonOnClickHandler(
          item as IButton,
          `${article.title}: ${item.title}`
        );
      };
      return (
        <MarketingDropdown
          options={linkItems}
          placeholder={title}
          onClick={onClick}
        />
      );
    }
    if (button) {
      return (
        <PrimaryButton
          onClick={() => buttonOnClickHandler(button)}
          size="normal"
          style={{ width: '100%' }}
        >
          <div className={styles.buttonLabel}>
            <ButtonText marginRight="one">{button.title}</ButtonText>
            <Icon iconType="arrowDownLong" />
          </div>
        </PrimaryButton>
      );
    }
    return buttons.map((btn) => (
      <PrimaryButton
        key={btn.navigationLink}
        onClick={() => buttonOnClickHandler(btn)}
        size="normal"
      >
        {btn.title}
      </PrimaryButton>
    ));
  };

  return (
    <div
      className={clsx(styles.sectionContainer, className, containerImageClass, {
        [styles.layoutTop]: article?.layout === SectionLayout.Top,
      })}
    >
      <div className={containerClass}>
        {article.media && (
          <div className={`${styles.imageContainer} ${imageClass}`}>
            <CustomImage
              className={clsx(styles.image, {
                [styles.imageWithArticleButton]: article?.buttons,
              })}
              src={article.media || ''}
              alt={article.title}
              width={imageWidth}
              height={imageHeight}
              isPresentation
            />
          </div>
        )}
        {article.title && article.longDescription && (
          <div className={`${styles.textContainer} ${textImageClass}`}>
            <div
              className={clsx(styles.header, {
                [styles.headerWithArticleButton]: article?.buttons,
              })}
            >
              <Subtitle className={styles.title}>{article.title}</Subtitle>
              {article.subTitle && (
                <LabelText className={clsx(styles.subtitle, styles.label)}>
                  {article.subTitle}
                </LabelText>
              )}
            </div>
            {article.subTitle && (
              <BodyText marginBottom="half" className={styles.subTitle}>
                {article.subTitle}
              </BodyText>
            )}
            {typeof article?.longDescription === 'string' ? (
              <BodyText>{article?.longDescription}</BodyText>
            ) : (
              <ContentfulRenderer highestHeadingSize={highestHeadingSize}>
                {article?.longDescription}
              </ContentfulRenderer>
            )}
            {article.infoBox && (
              <AlertBox type="info" OverrideIcon=" " className={styles.infoBox}>
                <ContentfulRenderer highestHeadingSize={highestHeadingSize}>
                  {article?.infoBox}
                </ContentfulRenderer>
              </AlertBox>
            )}
          </div>
        )}
        {(article.button || article.buttons || article.dropdownList) && (
          <div className={styles.buttonContainer}>{getButtonOrDropdown()}</div>
        )}
        {article.link && (
          <div className={styles.linkContainer}>
            {article.link.type === 'search criteria' ? (
              <ArrowSearchLink link={article.link} />
            ) : article.link?.hyperlink.includes(
                'https://agentportal.hurtigruten.com/learn/'
              ) ? (
              <LinkText
                to={`/learn/${article.link?.hyperlink?.replace(
                  'https://agentportal.hurtigruten.com/learn/',
                  ''
                )}`}
                as={Link}
              >
                {article.link.title}
              </LinkText>
            ) : (
              <LinkText href={article.link.hyperlink}>
                {article.link.title}
              </LinkText>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Section;
